import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';

import { Store, select } from '@ngrx/store';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, filter, map, take, tap, timeout } from 'rxjs/operators';

import { AppState } from '../../reducers';
import { selectAuthState } from '../_selectors/auth.selectors';
import { ModuleService } from '../../plant-configuration/_services/module.service';

import * as _ from 'lodash';

@Injectable()
export class RoleGuard implements CanActivate, CanActivateChild {
  constructor(
    private router: Router,
    private store: Store<AppState>,
    private moduleService: ModuleService
  ) {}

  private waitForModuleList$(): Observable<any[]> {
    const currentModules = this.moduleService.moduleList;

    if (Array.isArray(currentModules) && currentModules.length > 0) {
      return of(currentModules);
    }

    return this.moduleService.getAll().pipe(
      map((response: any) => response.data || response),
      tap((modules: any[]) => {
        this.moduleService.moduleList = modules;
      }),
      filter((modules: any[]) => Array.isArray(modules) && modules.length > 0),
      take(1),
      timeout(10000)
    );
  }

  private waitForAuthLoaded$(): Observable<any> {
    return this.store.pipe(
      select(selectAuthState),
      filter((auth) => !!auth && auth.isUserLoaded === true),
      take(1),
      tap((auth) => console.log('[AUTH] user retrieved', auth))
    );
  }

  private normalizePath(url: string): string {
    let path = url || '';

    path = path.split('?')[0];
    path = path.split('#')[0];

    path = path.replace('/add', '');
    path = path.replace('/list', '');
    path = path.split('/edit')[0];

    if (path !== path.split('/production/realtime-machines')[0]) {
      path = '/production/realtime-machines';
    }

    if (!path.startsWith('/')) {
      path = `/${path}`;
    }

    return path;
  }

  private getFallbackUrlTree(pathPermissions: any, groupId: number): UrlTree {
    const fallback = _.keys(pathPermissions).find(
      (p) => (pathPermissions[p] || []).includes(groupId)
    );

    return fallback
      ? this.router.parseUrl(fallback.startsWith('/') ? fallback : `/${fallback}`)
      : this.router.parseUrl('/auth');
  }

  private checkAccess(path: string): Observable<boolean | UrlTree> {
    const auth$ = this.waitForAuthLoaded$();
    const moduleList$ = this.waitForModuleList$();

    return combineLatest([auth$, moduleList$]).pipe(
      take(1),
      map(([auth, moduleList]) => {
        if (!auth.user) {
          return this.router.parseUrl('/auth');
        }

        const pathPermissions = this.moduleService.mapToPathGroupPermission(moduleList);
        const groupIdList: number[] = pathPermissions[path] || [];

        const authorized = groupIdList.includes(auth.user.group_id);

        if (authorized) {
          return true;
        }

        return this.getFallbackUrlTree(pathPermissions, auth.user.group_id);
      }),
      catchError((err) => {
        console.error('[RoleGuard] error antes de redirigir a auth:', err);
        return of(this.router.parseUrl('/auth'));
      })
    );
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    const path = this.normalizePath(state.url);
    return this.checkAccess(path);
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    const path = this.normalizePath(state.url);
    return this.checkAccess(path);
  }
}